import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import throttle from 'lodash/throttle';
import { useDialogMessage } from 'app/hooks';
import { AuthService } from 'app/services/authService';
import * as authActions from 'app/auth/store/actions';
import { toNumber } from 'lodash';
import { LAST_ACTIVITY_TIME_KEY } from './constants';

const EVENTS = [
  'mousemove',
  'mousedown',
  'mouseup',
  'click',
  'keydown',
  'keyup',
  'touchstart',
  'touchmove',
  'touchend',
  'wheel',
  'scroll',
  'pointerdown',
  'pointermove',
  'resize',
];
const WARNING_MS = 5 * 60 * 1000;
// Timers only CHECK the shared timestamp; correctness does not require a 1s tick.
const CHECK_INTERVAL_MS = 5000;
const ACTIVITY_THROTTLE_MS = 1000;

const readLastActivityTime = (storage = window.localStorage) => {
  const raw = storage.getItem(LAST_ACTIVITY_TIME_KEY);
  if (raw == null || raw === '') {
    return null;
  }
  const value = Number(raw);
  return Number.isNaN(value) ? null : value;
};

const writeLastActivityTime = (now = Date.now(), storage = window.localStorage) => {
  const current = readLastActivityTime(storage);
  if (current != null && now < current) {
    return current;
  }
  // Skip redundant writes that do not move the shared clock forward enough.
  if (current != null && now - current < ACTIVITY_THROTTLE_MS) {
    return current;
  }
  storage.setItem(LAST_ACTIVITY_TIME_KEY, String(now));
  return now;
};

const IdleTimer = (props) => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.pharmacy.update.configuration);
  const dialog = useDialogMessage('idle');
  const { t } = useTranslation();
  const dialogRef = useRef(dialog);
  const translateRef = useRef(t);
  const logoutMsRef = useRef(60000 * toNumber(data.logoutInactivityTime ?? 30));
  const loggingOutRef = useRef(false);
  const lastActivityRef = useRef(readLastActivityTime() ?? Date.now());

  dialogRef.current = dialog;
  translateRef.current = t;
  logoutMsRef.current = 60000 * toNumber(data.logoutInactivityTime ?? 30);

  useEffect(() => {
    const logOut = () => {
      if (loggingOutRef.current) {
        return;
      }
      loggingOutRef.current = true;
      dialogRef.current.close();
      AuthService.clearToken();
      dispatch(authActions.logoutUser());
      window.location.reload(); // Should re-init app after automatically logout
    };

    const showWarning = () => {
      if (dialogRef.current.isOpen || loggingOutRef.current) {
        return;
      }
      if (!AuthService.getToken()) {
        return;
      }
      const translate = translateRef.current;
      dialogRef.current
        .confirm(
          translate('IDLE_DIALOG_MESSAGE'),
          translate('IDLE_DIALOG_TITLE'),
          {
            buttons: [{ label: translate('BUTTON_CONTINUE') }],
            showCloseButton: false,
          },
          'error',
        )
        .on((label) => {
          if (label === translate('BUTTON_CONTINUE')) {
            const now = Date.now();
            lastActivityRef.current = now;
            writeLastActivityTime(now);
            dialogRef.current.close();
          }
        });
    };

    const getEffectiveLastTime = () => {
      const stored = readLastActivityTime();
      const memory = lastActivityRef.current;
      if (stored == null) {
        return memory;
      }
      return Math.max(stored, memory);
    };

    const evaluateIdleState = () => {
      if (loggingOutRef.current) {
        return;
      }
      if (!AuthService.getToken()) {
        return;
      }

      const lastTime = getEffectiveLastTime();
      if (lastTime == null) {
        const now = Date.now();
        lastActivityRef.current = now;
        writeLastActivityTime(now);
        return;
      }
      const elapsed = Date.now() - lastTime;
      // Logout at configured inactivity time; warn 5 minutes before that.
      const logoutAfter = logoutMsRef.current;
      const warningAfter = Math.max(0, logoutAfter - WARNING_MS);

      if (elapsed >= logoutAfter) {
        logOut();
        return;
      }

      if (elapsed >= warningAfter) {
        showWarning();
        return;
      }

      // Another tab advanced the clock: dismiss a stale local warning.
      if (dialogRef.current.isOpen) {
        dialogRef.current.close();
      }
    };

    // Activity only updates the shared clock. While the warning dialog is open,
    // ignore activity so only Continue can reset the idle timer.
    const onActivity = throttle(() => {
      if (dialogRef.current.isOpen || loggingOutRef.current) {
        return;
      }
      const now = Date.now();
      lastActivityRef.current = now;
      writeLastActivityTime(now);
    }, ACTIVITY_THROTTLE_MS);

    const onStorage = (event) => {
      if (event.key === LAST_ACTIVITY_TIME_KEY) {
        const remote = Number(event.newValue);
        if (!Number.isNaN(remote)) {
          lastActivityRef.current = Math.max(lastActivityRef.current || 0, remote);
        }
        evaluateIdleState();
        return;
      }

      if (event.key !== 'isLogined') {
        return;
      }

      const isLoginPage = window.location.pathname === '/auth/login';
      const isLogined = event.newValue === 'true';
      const hadToken = AuthService.getToken();

      // Another tab logged in: sync activity clock and enter the app from the login page.
      if (isLogined) {
        const now = Date.now();
        lastActivityRef.current = now;
        writeLastActivityTime(now);
        loggingOutRef.current = false;
        if (dialogRef.current.isOpen) {
          dialogRef.current.close();
        }
        if (isLoginPage) {
          window.location.reload();
        }
        return;
      }

      // Another tab logged out: leave authenticated pages.
      if (!isLogined && !hadToken && !isLoginPage) {
        logOut();
      }
    };

    const onVisibilityOrFocus = () => {
      evaluateIdleState();
    };

    EVENTS.forEach((ev) => {
      document.addEventListener(ev, onActivity, { passive: true });
    });
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    const intervalId = setInterval(evaluateIdleState, CHECK_INTERVAL_MS);
    evaluateIdleState();

    return () => {
      onActivity.cancel();
      EVENTS.forEach((ev) => {
        document.removeEventListener(ev, onActivity);
      });
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      clearInterval(intervalId);
    };
  }, [dispatch]);

  return props.children;
};

export default IdleTimer;
